"use client";

import React, { useState } from "react";
import { Badge } from "@/components/ui/feedback";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from "@/components/ui/card";
import {
  Label,
  Input,
  Select,
  ErrorMessage,
} from "@/components/ui/form-elements";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/overlays";
import { useRouter } from "next/navigation";
import Link from "next/link";
import {
  Search,
  Plus,
  Euro,
  Trash2,
  Tag,
  Laptop,
  TrendingUp,
  Users,
  Wrench,
  Building,
  Plane,
  HelpCircle,
  FolderOpen,
  Edit2,
  Calendar,
} from "lucide-react";
import {
  createCostAction,
  deleteCostAction,
  updateCostAction,
} from "@/actions/finance-actions";

interface CostItem {
  id: string;
  amount: number;
  category: string;
  description: string | null;
  createdAt: string;
  projectId: string | null;
  projectTitle: string | null;
}

interface ProjectItem {
  id: string;
  title: string;
}

interface CostsClientProps {
  initialCosts: CostItem[];
  projects: ProjectItem[];
}

const CATEGORY_PRESETS = [
  "Software & SaaS",
  "Marketing & Adv",
  "Consulenze & Servizi",
  "Hardware & Attrezzature",
  "Ufficio & Rent",
  "Viaggi & Trasferte",
  "Risorse Umane",
  "Altro",
];

export function CostsClient({ initialCosts, projects }: CostsClientProps) {
  const router = useRouter();
  const [costsList, setCostsList] = useState<CostItem[]>(initialCosts);
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");

  // Modal Form State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCost, setEditingCost] = useState<CostItem | null>(null);
  const [newProjectId, setNewProjectId] = useState("");
  const [newAmount, setNewAmount] = useState("");
  const [newCategory, setNewCategory] = useState("Software & SaaS");
  const [customCategory, setCustomCategory] = useState("");
  const [newDescription, setNewDescription] = useState("");

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Sync state
  React.useEffect(() => {
    setCostsList(initialCosts);
  }, [initialCosts]);

  const handleOpenModal = () => {
    setEditingCost(null);
    setNewProjectId("");
    setNewAmount("");
    setNewCategory("Software & SaaS");
    setCustomCategory("");
    setNewDescription("");
    setError("");
    setIsModalOpen(true);
  };

  const handleOpenEditModal = (cost: CostItem) => {
    setEditingCost(cost);
    setNewProjectId(cost.projectId || "");
    setNewAmount(cost.amount.toString());
    if (CATEGORY_PRESETS.includes(cost.category)) {
      setNewCategory(cost.category);
      setCustomCategory("");
    } else {
      setNewCategory("Altro");
      setCustomCategory(cost.category);
    }
    setNewDescription(cost.description || "");
    setError("");
    setIsModalOpen(true);
  };

  const handleCreateCost = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    const amountNum = parseFloat(newAmount);
    if (isNaN(amountNum) || amountNum <= 0) {
      setError("L'importo deve essere un numero valido maggiore di 0.");
      return;
    }

    const finalCategory =
      newCategory === "Altro" && customCategory.trim()
        ? customCategory.trim()
        : newCategory;

    if (!finalCategory) {
      setError("La categoria è obbligatoria.");
      return;
    }

    setLoading(true);
    try {
      if (editingCost) {
        const res = await updateCostAction(editingCost.id, {
          projectId: newProjectId || null,
          amount: amountNum,
          category: finalCategory,
          description: newDescription || null,
        });

        if (res.error) {
          setError(res.error);
        } else {
          setIsModalOpen(false);
          router.refresh();
        }
      } else {
        const res = await createCostAction({
          projectId: newProjectId || null,
          amount: amountNum,
          category: finalCategory,
          description: newDescription || null,
        });

        if (res.error) {
          setError(res.error);
        } else {
          setIsModalOpen(false);
          router.refresh();
        }
      }
    } catch (err: any) {
      setError(err.message || "Errore durante il salvataggio della spesa.");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCost = async (id: string) => {
    if (!confirm("Sei sicuro di voler eliminare questa spesa?")) return;
    try {
      const res = await deleteCostAction(id);
      if (res.error) {
        alert(res.error);
      } else {
        router.refresh();
      }
    } catch (err: any) {
      alert("Errore durante l'eliminazione: " + err.message);
    }
  };

  const filteredCosts = costsList.filter((cost) => {
    const matchesSearch =
      (cost.description &&
        cost.description.toLowerCase().includes(search.toLowerCase())) ||
      cost.category.toLowerCase().includes(search.toLowerCase()) ||
      (cost.projectTitle &&
        cost.projectTitle.toLowerCase().includes(search.toLowerCase()));

    const matchesCategory =
      categoryFilter === "all" || cost.category === categoryFilter;

    return matchesSearch && matchesCategory;
  });

  // Calculate totals
  const totalExpenses = costsList.reduce((sum, cost) => sum + cost.amount, 0);

  // Group by category for visualization
  const categoryTotalsMap = costsList.reduce(
    (map, cost) => {
      const cat = cost.category;
      map[cat] = (map[cat] || 0) + cost.amount;
      return map;
    },
    {} as Record<string, number>,
  );

  const categoryGroups = Object.entries(categoryTotalsMap)
    .map(([category, amount]) => ({
      category,
      amount,
      percentage:
        totalExpenses > 0 ? Math.round((amount / totalExpenses) * 100) : 0,
      count: costsList.filter((c) => c.category === category).length,
    }))
    .sort((a, b) => b.amount - a.amount);

  const formatCurrency = (val: number) => {
    return new Intl.NumberFormat("it-IT", {
      style: "currency",
      currency: "EUR",
      maximumFractionDigits: 0,
    }).format(val);
  };

  const formatDate = (dateStr: string) => {
    return new Intl.DateTimeFormat("it-IT", { dateStyle: "medium" }).format(
      new Date(dateStr),
    );
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case "Software & SaaS":
        return <Laptop className="h-4 w-4 text-primary-500" />;
      case "Marketing & Adv":
        return <TrendingUp className="h-4 w-4 text-success" />;
      case "Consulenze & Servizi":
        return <Users className="h-4 w-4 text-warning" />;
      case "Hardware & Attrezzature":
        return <Wrench className="h-4 w-4 text-info" />;
      case "Ufficio & Rent":
        return <Building className="h-4 w-4 text-purple-500" />;
      case "Viaggi & Trasferte":
        return <Plane className="h-4 w-4 text-orange-500" />;
      case "Risorse Umane":
        return <Users className="h-4 w-4 text-rose-500" />;
      default:
        return <HelpCircle className="h-4 w-4 text-text-secondary" />;
    }
  };

  return (
    <div className="space-y-6">
      {/* Overview stats & Categories summary list */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Total Expenses Card */}
        <Card className="flex flex-col justify-between border-t-4 border-t-error">
          <CardHeader>
            <CardDescription className="text-xs font-semibold uppercase tracking-wider">
              Uscite Totali
            </CardDescription>
            <CardTitle className="text-3xl font-extrabold tracking-tight mt-1 text-text-primary">
              {formatCurrency(totalExpenses)}
            </CardTitle>
          </CardHeader>
          <CardContent className="pb-6">
            <div className="text-xs text-text-secondary space-y-2 border-t border-border/40 pt-4 mt-2">
              <div className="flex justify-between">
                <span>Transazioni registrate:</span>
                <span className="font-semibold text-text-primary">
                  {costsList.length}
                </span>
              </div>
              <div className="flex justify-between">
                <span>Media per spesa:</span>
                <span className="font-semibold text-text-primary">
                  {costsList.length > 0
                    ? formatCurrency(totalExpenses / costsList.length)
                    : "€0"}
                </span>
              </div>
            </div>
            <Button
              onClick={handleOpenModal}
              className="w-full mt-6 gap-1.5 bg-error hover:bg-error-600 text-white"
            >
              <Plus className="h-4 w-4" /> Aggiungi Spesa
            </Button>
          </CardContent>
        </Card>

        {/* Categories Breakdown Card */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-base font-bold">
              Ripartizione Categorie
            </CardTitle>
            <CardDescription className="text-xs">
              Distribuzione delle spese correnti
            </CardDescription>
          </CardHeader>
          <CardContent className="max-h-[220px] overflow-y-auto pr-2 scrollbar-thin">
            {categoryGroups.length === 0 ? (
              <div className="text-center text-text-muted italic py-8 text-sm">
                Nessuna spesa registrata per generare le categorie.
              </div>
            ) : (
              <div className="space-y-3.5">
                {categoryGroups.map((group) => (
                  <div key={group.category} className="space-y-1">
                    <div className="flex items-center justify-between text-xs font-medium">
                      <div className="flex items-center gap-2">
                        {getCategoryIcon(group.category)}
                        <span className="text-text-primary font-semibold">
                          {group.category}
                        </span>
                        <span className="text-text-muted text-[10px]">
                          ({group.count} {group.count === 1 ? "voce" : "voci"})
                        </span>
                      </div>
                      <div className="space-x-2">
                        <span className="text-text-primary font-bold">
                          {formatCurrency(group.amount)}
                        </span>
                        <span className="text-text-muted text-[10px] bg-card-muted px-1.5 py-0.5 rounded border border-border/40">
                          {group.percentage}%
                        </span>
                      </div>
                    </div>
                    <div className="w-full bg-border rounded-full h-2 overflow-hidden">
                      <div
                        className="bg-error h-2 rounded-full"
                        style={{ width: `${group.percentage}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Search & Filters */}
      <div className="flex flex-col sm:flex-row gap-4 items-center justify-between bg-card p-4 rounded-xl border border-border">
        <div className="relative w-full sm:max-w-xs">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-text-muted" />
          <Input
            placeholder="Cerca descrizione, categoria o progetto..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>

        <div className="flex w-full sm:w-auto gap-3 items-center justify-between sm:justify-end">
          <Select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="w-[200px]"
          >
            <option value="all">Tutte le categorie</option>
            {Object.keys(categoryTotalsMap).map((cat) => (
              <option key={cat} value={cat}>
                {cat}
              </option>
            ))}
          </Select>

          <Button onClick={handleOpenModal} className="gap-1.5 shrink-0">
            <Plus className="h-4 w-4" />
            Nuova Spesa
          </Button>
        </div>
      </div>

      {/* Grid of Costs */}
      {filteredCosts.length === 0 ? (
        <div className="flex flex-col items-center justify-center p-12 border border-dashed border-border rounded-2xl bg-card/50 text-center max-w-xl mx-auto my-8">
          <FolderOpen className="h-10 w-10 text-primary-500 mb-3" />
          <h3 className="text-base font-bold text-text-primary">
            Nessuna spesa trovata
          </h3>
          <p className="text-sm text-text-secondary mt-1">
            Non ci sono transazioni corrispondenti ai filtri impostati.
          </p>
          <Button onClick={handleOpenModal} className="mt-4 gap-1.5">
            <Plus className="h-4 w-4" />
            Nuova Spesa
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredCosts.map((cost) => (
            <Card
              key={cost.id}
              className="relative overflow-hidden flex flex-col justify-between hover:border-primary-500/50 transition-all border-t-4 border-t-error group cursor-pointer"
              onClick={() => handleOpenEditModal(cost)}
            >
              {/* Hover Actions */}
              <div
                className="absolute top-3 right-3 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity z-10"
                onClick={(e) => e.stopPropagation()}
              >
                <Link
                  href={`/explore?nodeId=${cost.id}`}
                  title="Esplora relazioni"
                >
                  <Button
                    variant="secondary"
                    size="icon"
                    className="h-7 w-7 p-0 flex items-center justify-center"
                  >
                    <TrendingUp className="h-3.5 w-3.5 text-text-secondary hover:text-primary-600" />
                  </Button>
                </Link>
                <Button
                  variant="secondary"
                  size="icon"
                  onClick={() => handleOpenEditModal(cost)}
                  title="Modifica"
                  className="h-7 w-7 p-0 flex items-center justify-center"
                >
                  <Edit2 className="h-3.5 w-3.5 text-text-secondary hover:text-primary-600" />
                </Button>
                <Button
                  variant="secondary"
                  size="icon"
                  onClick={() => handleDeleteCost(cost.id)}
                  title="Elimina"
                  className="h-7 w-7 p-0 flex items-center justify-center hover:bg-error-soft hover:text-error"
                >
                  <Trash2 className="h-3.5 w-3.5 text-text-secondary" />
                </Button>
              </div>

              <CardHeader className="pb-3 pr-24">
                <div className="flex justify-between items-start gap-4">
                  <div>
                    <Badge
                      variant="neutral"
                      className="mb-1"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <span className="inline-flex items-center gap-1">
                        {getCategoryIcon(cost.category)}
                        {cost.category}
                      </span>
                    </Badge>
                    <h3 className="font-bold text-base text-text-primary leading-snug">
                      <Link
                        href={`/explore?nodeId=${cost.id}`}
                        className="hover:text-primary-600 hover:underline"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {cost.description || (
                          <span className="text-text-muted italic">
                            Spesa senza descrizione
                          </span>
                        )}
                      </Link>
                    </h3>
                  </div>

                  <span className="text-base font-bold text-error shrink-0">
                    -{formatCurrency(cost.amount)}
                  </span>
                </div>
              </CardHeader>

              <CardContent className="space-y-4 pb-4 flex-1 text-xs text-text-secondary">
                <div className="flex items-center gap-1.5">
                  <Calendar className="h-3.5 w-3.5 text-text-muted" />
                  <span>
                    Data: <strong>{formatDate(cost.createdAt)}</strong>
                  </span>
                </div>

                {cost.projectTitle ? (
                  <div className="flex items-center gap-1.5">
                    <Laptop className="h-3.5 w-3.5 text-text-muted" />
                    <span>
                      Progetto:{" "}
                      <Link
                        href={`/work/projects/${cost.projectId}`}
                        className="hover:underline text-primary-600 font-semibold"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {cost.projectTitle}
                      </Link>
                    </span>
                  </div>
                ) : (
                  <div className="flex items-center gap-1.5 text-text-muted italic">
                    <Laptop className="h-3.5 w-3.5 text-text-muted/40" />
                    <span>Nessun progetto collegato</span>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Add Cost Dialog */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingCost ? "Modifica Spesa" : "Nuova Spesa"}
            </DialogTitle>
            <DialogDescription>
              {editingCost
                ? "Aggiorna i dettagli della spesa operativa o di progetto registrata."
                : "Registra un costo operativo o di progetto per bilanciare i conti di cassa."}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleCreateCost} className="space-y-4 my-2">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="cost-category">Categoria</Label>
                <Select
                  id="cost-category"
                  value={newCategory}
                  onChange={(e) => setNewCategory(e.target.value)}
                  disabled={loading}
                >
                  {CATEGORY_PRESETS.map((cat) => (
                    <option key={cat} value={cat}>
                      {cat}
                    </option>
                  ))}
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="cost-amount">Importo (€)</Label>
                <Input
                  id="cost-amount"
                  type="number"
                  placeholder="0.00"
                  step="0.01"
                  value={newAmount}
                  onChange={(e) => setNewAmount(e.target.value)}
                  disabled={loading}
                  required
                />
              </div>
            </div>

            {newCategory === "Altro" && (
              <div className="space-y-1.5 animate-in fade-in slide-in-from-top-1 duration-200">
                <Label htmlFor="cost-custom-category">
                  Nome Categoria Personalizzata
                </Label>
                <Input
                  id="cost-custom-category"
                  placeholder="es. Tasse, Logistica Evento, ..."
                  value={customCategory}
                  onChange={(e) => setCustomCategory(e.target.value)}
                  disabled={loading}
                  required
                />
              </div>
            )}

            <div className="space-y-1.5">
              <Label htmlFor="cost-project">
                Progetto Collegato (Opzionale)
              </Label>
              <Select
                id="cost-project"
                value={newProjectId}
                onChange={(e) => setNewProjectId(e.target.value)}
                disabled={loading}
              >
                <option value="">Generale (Nessun progetto)</option>
                {projects.map((proj) => (
                  <option key={proj.id} value={proj.id}>
                    {proj.title}
                  </option>
                ))}
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="cost-description">Descrizione / Note</Label>
              <Input
                id="cost-description"
                placeholder="es. Rinnovo licenza mensile Slack Enterprise"
                value={newDescription}
                onChange={(e) => setNewDescription(e.target.value)}
                disabled={loading}
              />
            </div>

            {error && <ErrorMessage>{error}</ErrorMessage>}

            <div className="flex justify-end gap-2 pt-2">
              <Button
                type="button"
                variant="secondary"
                onClick={() => setIsModalOpen(false)}
                disabled={loading}
              >
                Annulla
              </Button>
              <Button
                type="submit"
                loading={loading}
                className="bg-error hover:bg-error-600 text-white"
              >
                {editingCost ? "Salva Modifiche" : "Registra Spesa"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
